/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { DestroyRef, Injectable, OnDestroy, OnInit, inject } from '@angular/core';
import { AuthActions, AuthService, AuthToken, OCC_USER_ID_CURRENT } from '@spartacus/core';
import {
  AuthenticationResult,
  EventMessage,
  EventType as MsalEventType,
} from '@azure/msal-browser';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { RequestLogout } from '@gea/digital-ui-lib';
import { Subject } from 'rxjs';
import { CustomUserAuthenticationTokenService } from './user-authentication-token.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class CustomAuthService extends AuthService implements OnInit, OnDestroy {
  protected userAuthTokenService = inject(CustomUserAuthenticationTokenService);
  private msalBroadcastService = inject(MsalBroadcastService);
  protected actions$ = inject(Actions);
  private destroyed$ = inject(DestroyRef);

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.actions$
      .pipe(ofActionDispatched(RequestLogout), takeUntil(this.destroy$))
      .subscribe(() => {
        this.coreLogout();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  loginWithIdToken(idToken: string) {
    this.userAuthTokenService
      .loadTokenUsingCustomFlow(idToken)
      .pipe(take(1))
      .subscribe((accessToken) => {
        this.authStorageService.setToken(accessToken as AuthToken);
        this.authStorageService.setItem(
          'access_expiry',
          `${new Date(new Date().getTime() + (accessToken?.expires_in ?? 1800) * 1000).getTime()}`,
        );
        this.authStorageService.setItem(
          'access_expiry',
          `${new Date(new Date().getTime() + 60100).getTime()}`,
        );
        this.userIdService.setUserId(OCC_USER_ID_CURRENT);
        this.store.dispatch(new AuthActions.Login());

        this.authRedirectService.redirect();
      });
  }

  /**
   * Check params in url and if there is an code/token then try to login with those.
   */
  override async checkOAuthParamsInUrl(): Promise<void> {
    try {
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) =>
              msg.eventType === MsalEventType.LOGIN_SUCCESS ||
              msg.eventType === MsalEventType.LOGIN_FAILURE ||
              msg.eventType === MsalEventType.ACQUIRE_TOKEN_SUCCESS ||
              msg.eventType === MsalEventType.ACQUIRE_TOKEN_FAILURE,
          ),
          takeUntilDestroyed(this.destroyed$),
        )
        .subscribe((result: EventMessage) => {
          const payload = result.payload as AuthenticationResult;
          const accessToken = this.authStorageService.getItem('access_token');
          if (payload?.idToken && !accessToken) {
            this.loginWithIdToken(payload.idToken);
          }
        });
    } catch {}
  }
}
